import React, { memo } from 'react';
import PercentTendencyFloat from '@gp/components/PercentTendencyFloat';
import { getFormatNumPlus, isValidNumber } from '@gp/utils/common';
import './index.scss';

interface IInDevMetricCellRow {
  value: string;
  label?: string;
  date?: string;
  // 该行 value 无有效数据时，日期位置展示的兜底文案（如 'NA'）
  noDataDate?: string;
}

interface IInDevMetricCellProps {
  type: string;
  data: any;
  align?: string;
  valueField?: string;
  momField?: string;
  dateField?: string;
  showMom?: boolean;
  rows?: IInDevMetricCellRow[];
}

const DEFAULT_PROPS: Partial<IInDevMetricCellProps> = {
  align: 'right',
  showMom: false,
  rows: [],
};

const formatValue = (raw: any): string => {
  if (!isValidNumber(raw)) {
    return '-';
  }
  return getFormatNumPlus(raw);
};

const InDevMetricCell = (props: IInDevMetricCellProps) => {
  const {
    type,
    data,
    align = 'right',
    valueField = '',
    momField = '',
    dateField = '',
    showMom = false,
    rows = [],
  } = props || {};

  const alignClass = align === 'right' ? 'is-align-right' : 'is-align-left';

  // value_mom：上下结构，上面 value，下面 MoM
  if (type === 'value_mom') {
    const momValue = data?.[momField];
    return (
      <div className={`dbs-indev-metric-cell ${alignClass}`}>
        <div className="dbs-indev-metric-cell-value">{formatValue(data?.[valueField])}</div>
        {showMom && (
          <div className="dbs-indev-metric-cell-tendency">
            <PercentTendencyFloat value={momValue} />
            <span className="dbs-indev-metric-cell-label">MoM</span>
          </div>
        )}
      </div>
    );
  }

  // value_date：上下结构，上面 value，下面 date
  if (type === 'value_date') {
    return (
      <div className={`dbs-indev-metric-cell ${alignClass}`}>
        <div className="dbs-indev-metric-cell-value">{formatValue(data?.[valueField])}</div>
        <div className="dbs-indev-metric-cell-label">{data?.[dateField] || '-'}</div>
      </div>
    );
  }

  // value_date_double / value_name_double / value_name_three：多行结构
  if (type === 'value_date_double' || type === 'value_name_double' || type === 'value_name_three') {
    return (
      <div className={`dbs-indev-metric-cell ${alignClass}`}>
        {rows.map((row, i) => {
          const dateText = row.date ? data?.[row.date] : '';
          // 该行 value 无有效数据时，日期位置展示兜底文案（如 NA）
          const dateDisplay = row.noDataDate && !isValidNumber(data?.[row.value])
            ? `(${row.noDataDate})`
            : dateText
              ? `(${dateText})`
              : '';
          return (
            <div className="dbs-indev-metric-cell-row" key={i}>
              <span className="dbs-indev-metric-cell-value">{formatValue(data?.[row.value])}</span>
              {row.label && (
                <span className={`dbs-indev-metric-cell-label dbs-indev-metric-cell-label--${row.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  {row.label}
                </span>
              )}
              {row.date && <span className="dbs-indev-metric-cell-label">{dateDisplay}</span>}
            </div>
          );
        })}
      </div>
    );
  }

  return <div className={`dbs-indev-metric-cell ${alignClass}`}>{formatValue(data?.[valueField])}</div>;
};

InDevMetricCell.defaultProps = DEFAULT_PROPS;

export default memo(InDevMetricCell);
